import { React, phone, email } from 'Imports';
import { useState, useEffect } from 'react';
import { Dialog, Paper, RadioGroup, FormControlLabel, TextField, Box, CircularProgress, DialogActions } from 'MaterialUIComponents';
import { StyledRadio } from '$Components/VideoEvents/VideoEventList/VideoEventListStyles';
import { VideoEventResponse } from '$Generated/api';
import { DriverNotificationService } from '$State/DriverNotificationFreezerService';
import { useAppSnackbar } from '$Utilities/hooks/useAppSnackbar';
import { AlertTypes } from '$Utilities/Enums/AlertTypes';
import * as DateFormatter from '$Components/Shared/DateFormatter';
import { isEncompass } from '$Utilities/integrationPlatform';
import CloseIcon from '@mui/icons-material/Close';
import VPButton from '$Components/Shared/Button/VPButton';

const InputMask = require('react-input-mask');

const styles = require('./ShareEventDialog.scss') as {
    dialogPaper: string;
    titleText: string;
    closeIcon: string;
    descriptionText: string;
    inputWrapper: string;
    dialogContentWrapper: string;
    dialogTitleRow: string;
    dialogTitle: string;
    dialogBody: string;
    dialogActions: string;
    radioGroup: string;
    inputContainer: string;
    input: string;
};

interface ShareEventDialogProps {
    open: boolean;
    videoEvent: VideoEventResponse;
    onClose: () => void;
    onSuccess: () => void;
}

const ShareEventDialog = ({ open, videoEvent, onClose, onSuccess }: ShareEventDialogProps) => {
    const [shareType, setShareType] = useState<'email' | 'smstext'>('email');
    const [inputValue, setInputValue] = useState(videoEvent.driverEmail ?? '');
    const [isLoading, setIsLoading] = useState(false);
    const { showSnackbarMessage } = useAppSnackbar();

    useEffect(() => {
        setShareType('email');
        setInputValue(videoEvent.driverEmail ?? '');
    }, [videoEvent.driverEmail, videoEvent.driverPhoneNumber]);

    const isSubmitEnabled =
        !isLoading && (shareType === 'email' ? email.isValidEmail(inputValue) : phone.normalizePhoneNumber(inputValue) !== null);

    const resetState = () => {
        setShareType('email');
        setInputValue(videoEvent.driverEmail ?? '');
    };

    const handleClose = () => {
        if (isLoading) return;
        resetState();
        onClose();
    };

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const type = event.target.value as 'email' | 'smstext';
        setShareType(type);
        setInputValue(type === 'email' ? (videoEvent.driverEmail ?? '') : (videoEvent.driverPhoneNumber ?? ''));
    };

    const handleShare = async () => {
        if (!isSubmitEnabled) return;
        setIsLoading(true);
        const timezoneOffsetHrs = new Date().getTimezoneOffset() / -60;
        const timezoneName = DateFormatter.getTimezoneString();
        try {
            if (shareType === 'email') {
                await DriverNotificationService.sendEmail(videoEvent.id!, inputValue, timezoneOffsetHrs, timezoneName);
            } else {
                await DriverNotificationService.sendText(
                    videoEvent.id!,
                    phone.normalizePhoneNumber(inputValue)!,
                    timezoneOffsetHrs,
                    timezoneName,
                );
            }
            showSnackbarMessage({ message: 'Video event shared successfully.', alertType: AlertTypes.Success });
            resetState();
            onSuccess();
        } catch {
            showSnackbarMessage({ message: 'Unable to share video event.', alertType: AlertTypes.Fail });
        } finally {
            setIsLoading(false);
        }
    };

    const actionButtons = (
        <>
            <VPButton
                variant={isEncompass ? 'outlined' : 'text'}
                onClick={handleClose}
                disabled={isLoading}
                text="Cancel"
                padding={isEncompass ? '9px 12px' : '0'}
            />
            <VPButton
                variant="filled"
                onClick={handleShare}
                disabled={!isSubmitEnabled}
                text="Share"
                isLoading={isLoading}
                padding={isEncompass ? '10px 12px' : '12px'}
                width={isEncompass ? 70 : 56}
            />
        </>
    );

    const inputField =
        shareType === 'email' ? (
            <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={inputValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
                placeholder="Enter email address"
                type="email"
                disabled={isLoading}
                InputProps={{ className: styles.inputContainer }}
                inputProps={{ className: styles.input }}
            />
        ) : (
            <InputMask
                mask="999-999-9999"
                maskChar=""
                value={inputValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
                disabled={isLoading}
            >
                {(inputProps: any) => (
                    <TextField
                        {...inputProps}
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder="Enter phone number"
                        InputProps={{ className: styles.inputContainer }}
                        inputProps={{ ...inputProps, className: styles.input }}
                    />
                )}
            </InputMask>
        );

    const titleContent = (
        <>
            <span className={styles.titleText}>Share Event</span>
            <CloseIcon onClick={handleClose} className={styles.closeIcon} />
        </>
    );

    const bodyContent = (
        <>
            <p className={styles.descriptionText}>
                Sharing this video event will give the recipient the ability to view, accept, or dispute this event.
            </p>
            <RadioGroup row value={shareType} onChange={handleRadioChange} className={styles.radioGroup}>
                <FormControlLabel
                    value="email"
                    slotProps={{ typography: { fontSize: '14px' } }}
                    control={<StyledRadio size="small" sx={{ padding: '8px' }} />}
                    label="Email"
                />
                <FormControlLabel
                    value="smstext"
                    slotProps={{ typography: { fontSize: '14px' } }}
                    control={<StyledRadio size="small" sx={{ padding: '8px' }} />}
                    label="SMS/Text"
                />
            </RadioGroup>
            <Box className={styles.inputWrapper}>{inputField}</Box>
        </>
    );

    return (
        <Dialog open={open} onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}>
            <Paper className={styles.dialogPaper}>
                {isEncompass && <Box className={styles.dialogTitle}>{titleContent}</Box>}

                <Box className={isEncompass ? styles.dialogBody : styles.dialogContentWrapper}>
                    {!isEncompass && <Box className={styles.dialogTitleRow}>{titleContent}</Box>}
                    {bodyContent}
                </Box>

                <DialogActions className={styles.dialogActions}>{actionButtons}</DialogActions>
            </Paper>
        </Dialog>
    );
};

export default ShareEventDialog;
